import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Drawer } from 'expo-router/drawer';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import { DrawerContentScrollView, DrawerItem } from "@react-navigation/drawer";
import { useSupabase } from "@/hooks/useSupabase";
import { trpc } from "@/trpc/react";
import { useCrew } from "@/context/CrewProvider";
import { View, Text, Image, StyleSheet, Platform, StatusBar, Linking } from 'react-native';
import React from 'react';
import colors from '@/constants/Colors';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// Custom drawer item list to override color behavior
// Routes that exist as files for routing/redirect purposes but should never
// appear in the drawer menu. We filter these here at render time INSTEAD of
// using `drawerItemStyle: { display: 'none' }` on their <Drawer.Screen>
// options, because that style is a known trigger of a react-native-screens
// 4.x + Fabric YogaLayoutableShadowNode crash on Android (see
// https://github.com/facebook/react-native/issues/52349).
const ALWAYS_HIDDEN_ROUTES = new Set(['+not-found']);

function CustomDrawerItemList(props: any) {
  const { state, descriptors, navigation, isInProbation } = props;

  return (
    <View>
      {state.routes.map((route: any, i: number) => {
        const focused = i === state.index;
        const { drawerLabel, drawerIcon, drawerItemStyle } = descriptors[route.key].options;

        // Skip hidden items (legacy support; see ALWAYS_HIDDEN_ROUTES note above)
        if (drawerItemStyle?.display === 'none') {
          return null;
        }

        // Skip routes that are always hidden from the menu
        if (ALWAYS_HIDDEN_ROUTES.has(route.name)) {
          return null;
        }

        // Skip my-probation unless the crew member is actually in probation
        if (route.name === 'my-probation' && !isInProbation) {
          return null;
        }

        // Skip auto-detected routes that haven't been explicitly registered
        // via <Drawer.Screen> (they have no drawerLabel set). These are
        // phantom items like `index` (a redirect) and `holding` (a screen
        // rendered manually by the layout) — without this filter they appear
        // as invisible black rows in the drawer that route to blank pages.
        if (!drawerLabel) {
          return null;
        }

        return (
          <DrawerItem
            key={route.key}
            label={({ focused }) => (
              <Text style={{ color: focused ? colors.accent : colors.text.primary, fontSize: 16 }}>
                {drawerLabel}
              </Text>
            )}
            icon={({ size }) => {
              const iconColor = focused ? colors.accent : colors.text.primary;
              if (drawerIcon) {
                return drawerIcon({ focused, size, color: iconColor });
              }
              return null;
            }}
            onPress={() => navigation.navigate(route.name)}
            focused={focused}
            activeTintColor={colors.accent}
            inactiveTintColor={colors.text.primary}
            activeBackgroundColor="rgba(255, 240, 0, 0.15)"
            style={[{ borderRadius: 0 }, drawerItemStyle]}
          />
        );
      })}
    </View>
  );
}

function CustomDrawerContent(props: any) {
  const { signOut } = useSupabase();
  const insets = useSafeAreaInsets();
  const { currentCrewMember } = useCrew();

  const { data: dutyManager } = trpc.settings.getDutyManager.useQuery();

  // Whether to show the My Probation menu item. Computed here so we can pass
  // it to CustomDrawerItemList for render-time filtering, instead of toggling
  // drawerItemStyle.display on the <Drawer.Screen> (which crashes Android).
  const isInProbation =
    currentCrewMember?.crew_levels?.[0]?.level?.probation_checklist_required === true &&
    !currentCrewMember?.probation_checklist_completed;

  // Weekdays: 5pm (17:00) to 8am (08:00). Weekends: 24 hours.
  const isDutyManagerHours = React.useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    const isWeekend = day === 0 || day === 6;
    return isWeekend || hour >= 17 || hour < 8;
  }, []);

  // Set status bar style to light (white text) for dark header
  React.useEffect(() => {
    StatusBar.setBarStyle('light-content');
    if (Platform.OS === 'android') {
      StatusBar.setBackgroundColor(colors.primary);
      StatusBar.setTranslucent(true);
    }
    return () => {
      StatusBar.setBarStyle('default');
      if (Platform.OS === 'android') {
        StatusBar.setTranslucent(false);
      }
    };
  }, []);

  // Additional padding for Android status bar if needed
  const statusBarPadding = Platform.OS === 'android' ? { paddingTop: insets.top } : {};

  const handleCallUsNow = () => {
    Linking.openURL('tel:01212899002');
  };

  const handleSignOut = () => {
    if (signOut) {
      signOut({ clearBiometric: true });
    }
  };

  return (
    <View style={[{ flex: 1 }, styles.drawerBackground, statusBarPadding]}>
      <View style={{ flex: 1, justifyContent: 'space-between' }}>
        <View style={{ flex: 1 }}>
          {/* Drawer Items */}
          <DrawerContentScrollView
            {...props}
            contentContainerStyle={styles.drawerContent}
            contentInsetAdjustmentBehavior="automatic"
          >
            <CustomDrawerItemList {...props} isInProbation={isInProbation} />

            {/* Logo moved inside the scrollable area */}
            <View style={styles.logoContainer}>
              <Image source={require("@/assets/images/new-logo.png")} style={{ width: 100, height: 190, opacity: 0.1 }} />
            </View>
          </DrawerContentScrollView>
        </View>

        {/* Bottom menu items - not absolute */}
        <View style={[styles.logoutContainer, { paddingBottom: insets.bottom }]}>
          {/* Duty Manager section - weekdays 5pm-8am, weekends all day */}
          {dutyManager?.staff && isDutyManagerHours && (
            <View style={styles.dutyManagerContainer}>
              <Text style={styles.dutyManagerText}>Duty Manager: {dutyManager?.staff?.display_name}</Text>
            </View>
          )}

          {/* Call us Now button */}
          <DrawerItem
            label={() => <Text style={{ color: colors.text.primary, fontSize: 16 }}>Call us Now</Text>}
            onPress={handleCallUsNow}
            icon={({ size }) => (<Ionicons name="call-outline" size={size} color={colors.text.primary} />)}
            style={styles.bottomMenuItem}
          />

          {/* Logout Button */}
          <DrawerItem
            label={() => <Text style={{ color: colors.text.primary, fontSize: 16 }}>Logout</Text>}
            onPress={handleSignOut}
            icon={({ size }) => (<MaterialIcons name="logout" size={size} color={colors.text.primary} />)}
            style={styles.logoutButton}
          />
        </View>
      </View>
    </View>
  );
}

// Styles for the drawer components
const styles = StyleSheet.create({
  drawerBackground: {
    backgroundColor: colors.primary,
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingVertical: 10,
    marginTop: 10,
  },
  drawerContent: {
    paddingTop: 10,
  },
  logoutContainer: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
    // paddingBottom: Platform.OS === 'ios' ? 30 : 15,
  },
  dutyManagerContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  dutyManagerText: {
    color: colors.text.primary,
    fontSize: 16,
    fontWeight: '600',
  },
  bottomMenuItem: {
    // Add any specific styling for bottom menu items
  },
  logoutButton: {
    // marginBottom: 10,
  },
});

export default function Layout() {
  const { currentCrewMember } = useCrew();
  const { data: notifications } = trpc.notification.list.useQuery({ crew_id: currentCrewMember?.id! });

  // Use getUnreadMessageCount instead of hasUnreadMessages — React Query deduplicates
  // this with the identical call in useBadgeCount, so zero additional server calls
  const { data: unreadMessageCount = 0 } = trpc.messages.getUnreadMessageCount.useQuery(
    { crew_id: currentCrewMember?.id! },
    {
      enabled: !!currentCrewMember?.id,
      refetchInterval: 30000,
    }
  );

  const unreadCount = notifications?.length || 0;
  const showMessageIndicator = unreadMessageCount > 0;

  // Check if crew member is in probation
  const isInProbation =
    currentCrewMember?.crew_levels?.[0]?.level?.probation_checklist_required === true &&
    !currentCrewMember?.probation_checklist_completed;

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <Drawer
        drawerContent={CustomDrawerContent}
        screenOptions={{
          drawerStyle: {
            backgroundColor: colors.primary,
          },
          drawerActiveTintColor: colors.accent,
          drawerInactiveTintColor: colors.text.primary,
          drawerActiveBackgroundColor: 'rgba(255, 240, 0, 0.15)',
          drawerItemStyle: {
            borderRadius: 0,
          },
          drawerLabelStyle: {
            color: undefined, // Let the tint color handle this
          },
          headerShown: false,
        }}
        screenListeners={{}}

        initialRouteName="home"
      >
        <Drawer.Screen
          name="home"
          options={{
            drawerLabel: 'Calendar',
            title: 'Calendar',
            drawerIcon: ({ focused, size, color }) => (<Ionicons name="calendar-outline" size={size} color={color} />),
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="timesheets"
          options={{
            drawerLabel: 'Timesheets',
            title: 'Timesheets',
            drawerIcon: ({ focused, size, color }) => (<Ionicons name="calendar-outline" size={size} color={color} />),
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="messages"
          options={{
            drawerLabel: 'Messages',
            title: 'Messages',
            drawerIcon: ({ focused, size, color }) => (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Ionicons name="chatbubble-outline" size={size} color={color} />
                {showMessageIndicator && (
                  <View style={{
                    backgroundColor: colors.success,
                    borderRadius: 10,
                    width: 10,
                    height: 10,
                    marginLeft: -10,
                    marginTop: -10
                  }} />
                )}
              </View>
            ),
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="notifications"
          options={{
            drawerLabel: 'Job Requests',
            title: 'Job Requests',
            drawerIcon: ({ focused, size, color }) => (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Ionicons name="briefcase-outline" size={size} color={color} />
                {unreadCount > 0 && (
                  <View style={{
                    backgroundColor: colors.error,
                    borderRadius: 10,
                    minWidth: 20,
                    height: 20,
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: -10,
                    marginTop: -10
                  }}>
                    <Text style={{ color: 'white', fontSize: 12 }}>{unreadCount}</Text>
                  </View>
                )}
              </View>
            ),
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="my-probation"
          options={{
            drawerLabel: 'My Probation',
            title: 'My Probation',
            drawerIcon: ({ focused, size, color }) => (<Ionicons name="list-outline" size={size} color={color} />),
            headerShown: false,
            // NOTE: visibility is controlled at render time in
            // CustomDrawerItemList (based on isInProbation). Do NOT use
            // drawerItemStyle: { display: 'none' } here — it triggers a known
            // Android Yoga assertion crash with the new architecture.
          }}
        />
        <Drawer.Screen
          name="uniform"
          options={{
            drawerLabel: 'Uniform',
            title: 'Uniform',
            drawerIcon: ({ focused, size, color }) => (<Ionicons name="shirt-outline" size={size} color={color} />),
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="settings"
          options={{
            drawerLabel: 'Settings',
            title: 'Settings',
            drawerIcon: ({ focused, size, color }) => (<Ionicons name="settings-outline" size={size} color={color} />),
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="whats-new"
          options={{
            drawerLabel: "What's New",
            title: "What's New",
            drawerIcon: ({ focused, size, color }) => (<Ionicons name="newspaper-outline" size={size} color={color} />),
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="about"
          options={{
            drawerLabel: 'About',
            title: 'About',
            drawerIcon: ({ focused, size, color }) => (<Ionicons name="information-circle-outline" size={size} color={color} />),
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="+not-found"
          options={{
            // Visibility controlled at render time via ALWAYS_HIDDEN_ROUTES
            // in CustomDrawerItemList.
          }}
        />
      </Drawer>
    </GestureHandlerRootView>
  );
}